/**
 * Our window size breakpoints in English form. Easier to think around compared to hard numbers.
 */
export enum WindowBreakpoint {
  DesktopAverageSize = 'average-desktop',
  DesktopSmallSize = 'small-desktop',
  MobileAverageSize = 'average-mobile',
}

/**
 * A grouping of related elements. Ideally, said grouping contains elements that all fall under a specific deviation
 * and viewing context.
 *
 * Some existing concepts that fit under this paradigm:
 * - "There are the buttons we show to buyers of an adoptable deviation."
 * - "These are the buttons we show to the seller of an adoptable deviation."
 */
export interface ElementGroup<ElementPlacementEnum> {
  /**
   * Some more general elements want to know the number of elements an {@link ElementGroup} will add to the bar. This allows
   * them to collapse into the more menu, making room for said {@link ElementGroup}'s more relevant elements.
   */
  countOnBar: number;
  /**
   * A group is usually in a self-contained component, so we use one field to determine
   * the resulting location of the group. The individual controls can be flipped on or off via `boolean` fields
   * in interfaces that extend {@link ElementGroup}.
   */
  groupPlacement: ElementPlacementEnum;
}

export type ElementPlacementMap<
  ElementPlacementEnum,
  BaseElementsType extends {
    [key: string]: ElementPlacementEnum | ElementPlacementEnum[];
  } = {},
  ElementGroupsType extends {
    [key: string]: ElementGroup<ElementPlacementEnum>;
  } = { [key: string]: ElementGroup<ElementPlacementEnum> },
  MetaType = { [key: string]: any },
> = BaseElementsType & {
  /**
   * These are collections of buttons that we show given a certain context (e.g. is this an adoptable?).
   *
   * They can take up large amounts of space on the action bar, so tracking them separately from the rest will
   * make it easy for us to collapse or remove other less relevant controls. Ideally, only one of these groups
   * should be active at a given time.
   */
  elementGroups: ElementGroupsType;

  /**
   * There's some meta information that fits within the domain of the ElementPlacementMap. For example, should we render a
   * "more actions" menu?
   *
   * NOTE: This is not a dumping ground. Think over your options thoroughly before adding more fields to this object.
   */
  meta: MetaType;
};
